import React, { useCallback, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack } from "@mui/material";
import { RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard, Props as EmptyCardProps } from "components/Empty-card/EmptyCard";
import { DealCreate } from "components/modals";
import {
  Banner,
  Dashboard,
  DealListTable,
  DealsToolbar,
  Grid,
  GroupingViewProvider,
  Pipeline,
  SharedList,
  Table,
  Views
} from "components/My-deals";
import { DealViews } from "constants/deal";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useCanCreateDeal, useNavigate, usePrompt } from "hooks";
import {
  useAccountSelector,
  useDealActions,
  useDealQueriesSelect,
  usePickUserPermissions,
  useShowUnlockFullAccessSelect,
  useTaskDealsSelect,
  useUserRoleSelector
} from "store";
import { UserPermissionType } from "store/types";

import EmptyCardAnalytics from "../../assets/images/EmptyCardAnalytics.png";
import EmptyCardDealsCard from "../../assets/images/EmptyCardDealsCard.png";
import EmptyCardDealsPipeline from "../../assets/images/EmptyCardDealsPipeline.png";
import EmptyCardList from "../../assets/images/EmptyCardList.png";
import { RoleName } from "../../constants";

type EmptyStateItem = Pick<EmptyCardProps, "title" | "description" | "image">;

const emptyStateConfig: Record<DealViews, EmptyStateItem> = {
  [DealViews.Dashboard]: {
    title: EMPTY_CARD_CONTENT.myDealsDashboard.title,
    description: {
      text: EMPTY_CARD_CONTENT.myDealsDashboard.description,
      externalText: EMPTY_CARD_CONTENT.myDealsDashboard.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardAnalytics, sx: { width: 315, height: 140 } }
  },
  [DealViews.PIPELINE]: {
    title: EMPTY_CARD_CONTENT.myDealsPipeline.title,
    description: {
      text: EMPTY_CARD_CONTENT.myDealsPipeline.description,
      externalText: EMPTY_CARD_CONTENT.myDealsPipeline.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardDealsPipeline, sx: { width: 315, height: 148 } }
  },
  [DealViews.GRID]: {
    title: EMPTY_CARD_CONTENT.myDealsGrid.title,
    description: {
      text: EMPTY_CARD_CONTENT.myDealsGrid.description,
      externalText: EMPTY_CARD_CONTENT.myDealsGrid.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardDealsCard, sx: { width: 315, height: 144 } }
  },
  [DealViews.TABLE]: {
    title: EMPTY_CARD_CONTENT.myDealsTable.title,
    description: {
      text: EMPTY_CARD_CONTENT.myDealsTable.description,
      externalText: EMPTY_CARD_CONTENT.myDealsTable.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardList, sx: { width: 315, height: 142 } }
  }
};

export const MyDeals: React.FC = () => {
  const { updateQueries } = useDealActions();

  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const userRole = useUserRoleSelector();
  const showUnlockFullAccess = useShowUnlockFullAccessSelect();
  const myDeals = usePickUserPermissions("deals");
  const canCreateDeal = useCanCreateDeal();

  const {
    allow_news: isDashboardEnabled,
    show_hcs_view,
    allow_deals_grouping: isDealsGroupingEnabled
  } = useAccountSelector() || {};

  // The grouped/table view: the new grouped table when grouping is enabled, otherwise the classic table.
  const views: Record<DealViews, JSX.Element> = {
    [DealViews.Dashboard]: <Dashboard />,
    [DealViews.PIPELINE]: <Pipeline />,
    [DealViews.GRID]: <Grid />,
    [DealViews.TABLE]: isDealsGroupingEnabled ? <DealListTable /> : <Table />
  };
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const hideTabs = show_hcs_view && userRole?.name === RoleName.Investor;
  const deals = useTaskDealsSelect();

  const { view } = useDealQueriesSelect();

  const handleAddDeal = useCallback(async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });
    if (!data) return;
    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt, navigate]);

  const isPipelineAvailable: boolean = useMemo(() => {
    if (!userRole) return false;
    return (
      userRole.name === RoleName.Admin ||
      userRole.name === RoleName.Analyst ||
      userRole.name === RoleName.TeamViewer ||
      userRole.name === RoleName.Investor
    );
  }, [userRole]);

  return (
    <Stack sx={{ width: "100%", overflow: "hidden", p: "1px", gap: "16px" }} alignItems="stretch" direction="column">
      {showUnlockFullAccess && <Banner />}
      <RoleComponentGuard
        availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Investor]}
        hideChild={noAccessPermission}
        RenderInstead={
          <RoleComponentGuard
            availableRoles={[RoleName.Guest, RoleName.Viewer]}
            RenderInstead={
              <AccessDenied
                redirectClick={() => navigate({ route: isDashboardEnabled ? "dashboard" : "reports" })}
                title="Back Home"
              />
            }
          >
            <SharedList />
          </RoleComponentGuard>
        }
      >
        <GroupingViewProvider>
          {!hideTabs && (
            <Views
              value={view}
              onChange={(view) => updateQueries({ view })}
              isPipelineAvailable={isPipelineAvailable}
            />
          )}
          {view !== DealViews.Dashboard && deals.length > 0 && <DealsToolbar />}
          {deals.length > 0 ? (
            <Box overflow="hidden" display="flex" flexGrow={1}>
              {views[view]}
            </Box>
          ) : (
            <EmptyCard
              {...emptyStateConfig[view]}
              fitPage
              actionsElement={
                canCreateDeal ? (
                  <Button
                    variant="contained"
                    onClick={handleAddDeal}
                    startIcon={<AddIcon />}
                    sx={{ bgcolor: "#071F35" }}
                  >
                    Add Deal
                  </Button>
                ) : undefined
              }
            />
          )}
        </GroupingViewProvider>
      </RoleComponentGuard>
    </Stack>
  );
};
